import React from 'react';
import LineGraph from '../../../../../components/linegraph';
import {
  getDateRange,
  getTrendIndicator,
  getMonthYearRange,
} from '../../../../../utils/performanceUtils';
import {
  KPI_DECIMAL_PLACES,
  KPI_PERCENTAGE_DECIMAL_PLACES,
} from '../../../../../constants/constants';
import lang from '../../../../../languages/en.json';
import { useBuildDynamicUrl } from '../../../../../hooks/useDynamicUrl';
import { useClickHandler } from '../../../../../utils/usageMetricsLogger';
import { useAppSelector } from '../../../../../redux/hooks';
import { selectOverallPerformanceData } from '../../../../../redux/PerformanceReducer/Overall/overallSlice';

const BASE_URL_TYPE = 'WFC';
const PAGE_NM = 'Performance';
const TAB_NM = 'Overall Performance';

/**
 * InStoreAndEcomGraphs component displays in-store and e-commerce performance line graphs
 * Accesses performance data directly from Redux store
 */
const InStoreAndEcomGraphs: React.FC = (): JSX.Element => {
  const data = useAppSelector(selectOverallPerformanceData);
  const totalSales = data?.TSALE;
  const sales = data?.SALES;
  const ecomSales = data?.ESALE;
  const salesPerSqFt = data?.SLSSF;
  const grossProfit = data?.GP;
  const ecomGrossProfit = data?.EGP;
  const grossProfitPerSqFt = data?.GPSF;
  const grossMargin = data?.GM;
  const ecomGrossMargin = data?.EGM;
  const avgTransaction = data?.ATS;
  const transactionCount = data?.TCNT;
  const ecomTransactionCount = data?.ECNT;
  const unitsPerTransaction = data?.TUNIT;
  const inventoryValue = data?.IVALU;

  const buildDynamicUrl = useBuildDynamicUrl();
  const handleAndLogClick = useClickHandler(PAGE_NM, TAB_NM);

  return (
    <>
      <LineGraph
        title={totalSales?.title}
        mainValue={totalSales?.currentYtdAmount}
        format={totalSales?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[totalSales?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[totalSales?.kpiCode]}
        datasets={totalSales?.datasets}
        trendIndicator={getTrendIndicator(totalSales)}
        dateRange={getDateRange(totalSales)}
      />
      <LineGraph
        title={sales?.title}
        mainValue={sales?.currentYtdAmount}
        format={sales?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[sales?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[sales?.kpiCode]}
        datasets={sales?.datasets}
        trendIndicator={getTrendIndicator(sales)}
        dateRange={getDateRange(sales)}
        viewLinks={[
          {
            label: lang.lineGraph.viewByDept,
            onClick: () => {
              const url = buildDynamicUrl(BASE_URL_TYPE, sales?.kpiCode, 'dept');
              handleAndLogClick(
                `In-Store and Ecom - ${sales?.title} - ${lang.lineGraph.viewByDept}`,
                { url, urlTarget: '_blank' },
              );
            },
          },
          {
            label: lang.lineGraph.viewByMC,
            onClick: () => {
              const url = buildDynamicUrl(BASE_URL_TYPE, sales?.kpiCode, 'merchClass');
              handleAndLogClick(
                `In-Store and Ecom - ${sales?.title} - ${lang.lineGraph.viewByMC}`,
                { url, urlTarget: '_blank' },
              );
            },
          },
        ]}
      />
      <LineGraph
        title={ecomSales?.title}
        mainValue={ecomSales?.currentYtdAmount}
        format={ecomSales?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[ecomSales?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[ecomSales?.kpiCode]}
        datasets={ecomSales?.datasets}
        trendIndicator={getTrendIndicator(ecomSales)}
        dateRange={getDateRange(ecomSales)}
      />
      <LineGraph
        title={salesPerSqFt?.title}
        mainValue={salesPerSqFt?.currentYtdAmount}
        format={salesPerSqFt?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[salesPerSqFt?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[salesPerSqFt?.kpiCode]}
        datasets={salesPerSqFt?.datasets}
        trendIndicator={getTrendIndicator(salesPerSqFt)}
        dateRange={getDateRange(salesPerSqFt)}
      />
      <LineGraph
        title={grossProfit?.title}
        mainValue={grossProfit?.currentYtdAmount}
        format={grossProfit?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[grossProfit?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[grossProfit?.kpiCode]}
        datasets={grossProfit?.datasets}
        trendIndicator={getTrendIndicator(grossProfit)}
        dateRange={getDateRange(grossProfit)}
        viewLinks={[
          {
            label: lang.lineGraph.viewByDept,
            onClick: () => {
              const url = buildDynamicUrl(BASE_URL_TYPE, grossProfit?.kpiCode, 'dept');
              handleAndLogClick(
                `In-Store and Ecom - ${grossProfit?.title} - ${lang.lineGraph.viewByDept}`,
                { url, urlTarget: '_blank' },
              );
            },
          },
          {
            label: lang.lineGraph.viewByMC,
            onClick: () => {
              const url = buildDynamicUrl(BASE_URL_TYPE, grossProfit?.kpiCode, 'merchClass');
              handleAndLogClick(
                `In-Store and Ecom - ${grossProfit?.title} - ${lang.lineGraph.viewByMC}`,
                { url, urlTarget: '_blank' },
              );
            },
          },
        ]}
      />
      <LineGraph
        title={ecomGrossProfit?.title}
        mainValue={ecomGrossProfit?.currentYtdAmount}
        format={ecomGrossProfit?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[ecomGrossProfit?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[ecomGrossProfit?.kpiCode]}
        datasets={ecomGrossProfit?.datasets}
        trendIndicator={getTrendIndicator(ecomGrossProfit)}
        dateRange={getDateRange(ecomGrossProfit)}
      />
      <LineGraph
        title={grossProfitPerSqFt?.title}
        mainValue={grossProfitPerSqFt?.currentYtdAmount}
        format={grossProfitPerSqFt?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[grossProfitPerSqFt?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[grossProfitPerSqFt?.kpiCode]}
        datasets={grossProfitPerSqFt?.datasets}
        trendIndicator={getTrendIndicator(grossProfitPerSqFt)}
        dateRange={getDateRange(grossProfitPerSqFt)}
      />
      <LineGraph
        title={grossMargin?.title}
        mainValue={grossMargin?.currentYtdAmount}
        format={grossMargin?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[grossMargin?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[grossMargin?.kpiCode]}
        datasets={grossMargin?.datasets}
        trendIndicator={getTrendIndicator(grossMargin)}
        dateRange={getDateRange(grossMargin)}
        viewLinks={[
          {
            label: lang.lineGraph.viewByDept,
            onClick: () => {
              const url = buildDynamicUrl(BASE_URL_TYPE, grossMargin?.kpiCode, 'dept');
              handleAndLogClick(
                `In-Store and Ecom - ${grossMargin?.title} - ${lang.lineGraph.viewByDept}`,
                { url, urlTarget: '_blank' },
              );
            },
          },
          {
            label: lang.lineGraph.viewByMC,
            onClick: () => {
              const url = buildDynamicUrl(BASE_URL_TYPE, grossMargin?.kpiCode, 'merchClass');
              handleAndLogClick(
                `In-Store and Ecom - ${grossMargin?.title} - ${lang.lineGraph.viewByMC}`,
                { url, urlTarget: '_blank' },
              );
            },
          },
        ]}
      />
      <LineGraph
        title={ecomGrossMargin?.title}
        mainValue={ecomGrossMargin?.currentYtdAmount}
        format={ecomGrossMargin?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[ecomGrossMargin?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[ecomGrossMargin?.kpiCode]}
        datasets={ecomGrossMargin?.datasets}
        trendIndicator={getTrendIndicator(ecomGrossMargin)}
        dateRange={getDateRange(ecomGrossMargin)}
      />
      <LineGraph
        title={avgTransaction?.title}
        mainValue={avgTransaction?.currentYtdAmount}
        format={avgTransaction?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[avgTransaction?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[avgTransaction?.kpiCode]}
        datasets={avgTransaction?.datasets}
        trendIndicator={getTrendIndicator(avgTransaction)}
        dateRange={getDateRange(avgTransaction)}
      />
      <LineGraph
        title={transactionCount?.title}
        mainValue={transactionCount?.currentYtdAmount}
        format={transactionCount?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[transactionCount?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[transactionCount?.kpiCode]}
        datasets={transactionCount?.datasets}
        trendIndicator={getTrendIndicator(transactionCount)}
        dateRange={getDateRange(transactionCount)}
        viewLinks={[
          {
            label: lang.lineGraph.viewByDept,
            onClick: () => {
              const url = buildDynamicUrl(BASE_URL_TYPE, transactionCount?.kpiCode, 'dept');
              handleAndLogClick(
                `In-Store and Ecom - ${transactionCount?.title} - ${lang.lineGraph.viewByDept}`,
                { url, urlTarget: '_blank' },
              );
            },
          },
          {
            label: lang.lineGraph.viewByMC,
            onClick: () => {
              const url = buildDynamicUrl(BASE_URL_TYPE, transactionCount?.kpiCode, 'merchClass');
              handleAndLogClick(
                `In-Store and Ecom - ${transactionCount?.title} - ${lang.lineGraph.viewByMC}`,
                { url, urlTarget: '_blank' },
              );
            },
          },
        ]}
      />
      <LineGraph
        title={ecomTransactionCount?.title}
        mainValue={ecomTransactionCount?.currentYtdAmount}
        format={ecomTransactionCount?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[ecomTransactionCount?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[ecomTransactionCount?.kpiCode]}
        datasets={ecomTransactionCount?.datasets}
        trendIndicator={getTrendIndicator(ecomTransactionCount)}
        dateRange={getDateRange(ecomTransactionCount)}
      />
      <LineGraph
        title={unitsPerTransaction?.title}
        mainValue={unitsPerTransaction?.currentYtdAmount}
        format={unitsPerTransaction?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[unitsPerTransaction?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[unitsPerTransaction?.kpiCode]}
        datasets={unitsPerTransaction?.datasets}
        trendIndicator={getTrendIndicator(unitsPerTransaction)}
        dateRange={getDateRange(unitsPerTransaction)}
      />

      <LineGraph
        title={inventoryValue?.title}
        mainValue={inventoryValue?.currentYtdAmount}
        format={inventoryValue?.format}
        decimalPlaces={KPI_DECIMAL_PLACES[inventoryValue?.kpiCode]}
        percentageDecimalPlaces={KPI_PERCENTAGE_DECIMAL_PLACES[inventoryValue?.kpiCode]}
        datasets={inventoryValue?.datasets}
        trendIndicator={getTrendIndicator(inventoryValue)}
        dateRange={getMonthYearRange(inventoryValue)}
      />
    </>
  );
};

export default InStoreAndEcomGraphs;
